import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import React, { useState } from 'react'

import { useTypedDispatch } from '../..'
import { commentSubmitSuccess, submitChallenge } from '../../actions/energyEfficiency'
import { MOMENT_ENERGY_EFFICIENCY_COMMENT_DATE_FORMAT } from '../../constants'
import moment from '../../lib/moment-fi'
import { color, defaultTextColor, theme } from '../../Theme'
import { EnergyEfficiencyLeg } from '../../types/Input'

const CommentLink = styled.div<{ isActive?: boolean }>`
  color: ${(p) => (p.isActive ? defaultTextColor : color('primaryBlue'))};
  cursor: pointer;
  text-transform: ${(p) => (p.isActive ? 'none' : 'uppercase')};
  margin-top: 4px;
  width: 100%;
  text-align: ${(p) => (p.isActive ? 'left' : 'center')};
  ${(p) => theme.text('normal', 'content', p.isActive ? 'normal' : 'bold')};
`

const CommentContainer = styled.div`
  margin-top: 8px;
  width: 80%;
  position: relative;
`

const SelectContainer = styled.div`
  width: 95%;
  position: relative;
  margin-bottom: 8px;
  height: 44px;
`

const SelectLabel = styled.label`
  position: absolute;
  top: 0px;
  left: 12px;
  font-size: 0.6rem;
  color: ${color('grayDark')};
  text-transform: uppercase;
  z-index: 2;
  pointer-events: none;
`

const StyledSelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 18px 10px 5px 10px;
  border: 1px solid ${color('grayLight')};
  border-radius: 4px;
  background: white;
  box-sizing: border-box;
  appearance: revert;
  font-size: 14px;
  line-height: 1;
`

const CommentTextarea = styled.textarea`
  padding: 6px;
  margin-top: 8px;
  border: 1px solid ${color('grayLight')};
  border-radius: 4px;
  width: 95%;
  height: 44px;
  resize: none;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 14px;
`

const SendButtonWrapper = styled.div`
  position: absolute;
  right: -60px;
  bottom: 0;
  height: 44px;
  display: flex;
  align-items: center;
`

const SendButton = styled.div<{ disabled: boolean }>`
  color: ${(p) => (p.disabled ? color('grayLight') : color('primaryBlue'))};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  text-transform: uppercase;
  white-space: nowrap;
  padding-left: 20px;
  ${theme.text('normal', 'content', 'bold')};
`

const CommentItem = styled.div`
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${color('grayLighter')};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const CommentText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${defaultTextColor};
  word-wrap: break-word;
`

const CommentHeader = styled.span`
  font-weight: bold;
`

const CommentsWrapper = styled.div`
  margin-top: 10px;
  margin-bottom: 15px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

type EnergyEfficiencyCommentsProps = {
  shiftId: string
  shiftDate: string
  leg: EnergyEfficiencyLeg
  legIndex: number
  isOpen: boolean
  onToggle: (index: number) => void
  t: TFunction
}

const EnergyEfficiencyComments = ({
  shiftId,
  shiftDate,
  leg,
  legIndex,
  isOpen,
  onToggle,
  t,
}: EnergyEfficiencyCommentsProps) => {
  const dispatch = useTypedDispatch()

  const [commentText, setCommentText] = useState('')
  const [selectedOption, setSelectedOption] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const commentOptions = [
    { value: 'energy', label: t('energyCommentCategory.energy') },
    { value: 'timetable', label: t('energyCommentCategory.timetable') },
    { value: 'work', label: t('energyCommentCategory.work') },
    { value: 'other', label: t('energyCommentCategory.other') },
  ]

  const handleSendComment = async () => {
    if (!selectedOption || !commentText.trim() || submitting) return

    setSubmitting(true)

    try {
      const result = await submitChallenge(shiftId, shiftDate, selectedOption, commentText, leg)

      if (result.success && result.comment) {
        dispatch(commentSubmitSuccess(shiftId, legIndex, result.comment))
        onToggle(null)
        setCommentText('')
        setSelectedOption('')
      } else {
        console.error('Comment submission failed:', result.error)
      }
    } catch (err) {
      console.error('Comment submission error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {leg.comments && leg.comments.length > 0 && (
        <CommentsWrapper>
          {leg.comments.map((comment, index) => {
            const formattedDate = moment(comment.created).format(
              MOMENT_ENERGY_EFFICIENCY_COMMENT_DATE_FORMAT
            )
            const category = t(`energyCommentCategory.${comment.category}`, comment.category)

            return (
              <CommentItem key={index}>
                <CommentText>
                  <CommentHeader>
                    {t('you')}, {formattedDate}, {category}:
                  </CommentHeader>
                  {comment.comment}
                </CommentText>
              </CommentItem>
            )
          })}
        </CommentsWrapper>
      )}

      <Row>
        <CommentLink isActive={isOpen} onClick={() => onToggle(legIndex)}>
          {t('addComment')}
        </CommentLink>
      </Row>

      {isOpen && (
        <CommentContainer>
          <SelectContainer>
            <SelectLabel>{t('subject')}</SelectLabel>
            <StyledSelect
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              <option value="" disabled hidden>
                {t('selectSubject')}
              </option>
              {commentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </SelectContainer>

          <CommentTextarea
            placeholder={t('energyEfficiency.describeEnergyEfficiency')}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />

          <SendButtonWrapper>
            <SendButton
              disabled={!selectedOption || !commentText.trim() || submitting}
              onClick={handleSendComment}
            >
              {t('action.submit')}
            </SendButton>
          </SendButtonWrapper>
        </CommentContainer>
      )}
    </>
  )
}

export default EnergyEfficiencyComments
